import { getInitials } from '../lib/initials';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getUserById } from '../services/users';
import { useAuth } from '../hooks/useAuth';
import { TipOfTheDay } from '../components/TipOfTheDay';

export default function ProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const { user: currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    navigate('/login');
    Promise.resolve().then(() => logout());
  };
  
  const { data: user, isLoading: userLoading, error: userError } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => getUserById(userId!),
    enabled: !!userId,
  });

  if (userLoading) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="bg-white rounded-lg shadow p-6 mb-6 animate-pulse">
          <div className="flex items-start gap-4">
            <div className="w-20 h-20 bg-gray-300 rounded-full"></div>
            <div className="flex-1">
              <div className="h-6 bg-gray-300 rounded w-32 mb-2"></div>
              <div className="h-4 bg-gray-300 rounded w-48"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (userError || !user) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
          Kunne ikke indlæse profil
        </div>
      </div>
    );
  }

  const isOwnProfile = currentUser?.id === user.id;

  return (
    <div className="max-w-2xl mx-auto p-4">
      {isOwnProfile && <TipOfTheDay />}
      {/* Profile Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start gap-4 mb-4">
          {user.profileImageUrl ? (
            <img
              src={user.profileImageUrl}
              alt={user.name}
              className="w-20 h-20 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-20 h-20 bg-blue-500 text-white rounded-full flex-shrink-0 flex items-center justify-center text-2xl font-semibold">
              {getInitials(user.name)}
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
              <h1 className="text-2xl font-bold text-gray-900 break-words">{user.name}</h1>
              <div className="flex flex-wrap gap-1.5 flex-shrink-0">
                {isOwnProfile ? (
                  <>
                    <Link
                      to="/edit-profile"
                      className="btn btn-primary"
                      style={{ fontSize: '0.8rem', padding: '0.3em 0.8em' }}
                    >
                      Rediger profil
                    </Link>
                    {currentUser?.isAdmin && (
                      <Link
                        to="/admin/users"
                        className="btn"
                        style={{ fontSize: '0.8rem', padding: '0.3em 0.8em' }}
                      >
                        🔧 Admin
                      </Link>
                    )}
                    <Link
                      to="/archive"
                      className="btn"
                      style={{ fontSize: '0.8rem', padding: '0.3em 0.8em' }}
                    >
                      Arkiv
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="btn"
                      style={{ fontSize: '0.8rem', padding: '0.3em 0.8em', background: '#ef4444', color: '#fff' }}
                    >
                      Log ud
                    </button>
                  </>
                ) : (
                  <Link
                    to={`/messages/${user.id}`}
                    className="btn btn-primary"
                    style={{ fontSize: '0.8rem', padding: '0.3em 0.8em' }}
                  >
                    Send besked
                  </Link>
                )}
              </div>
            </div>
            {user.bio && (
              <p className="text-gray-600 mt-1">{user.bio}</p>
            )}
            {/* Contact info */}
            {(user.phone || user.contactEmail) && (
              <div className="mt-2 space-y-1">
                {user.phone && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <a href={`tel:${user.phone}`} className="hover:text-primary-600">{user.phone}</a>
                  </div>
                )}
                {user.contactEmail && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <a href={`mailto:${user.contactEmail}`} className="hover:text-primary-600">{user.contactEmail}</a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
